import React, { useMemo, useState } from "react";
import {
    Button,
    Popconfirm,
    Space,
    Table,
    Tag,
    Tooltip,
    Badge,
} from "antd";
import {
    CheckOutlined,
    DeleteOutlined,
    EditOutlined,
    DownloadOutlined,
    EyeOutlined,
} from "@ant-design/icons";
import {
    formatAmount,
    formatDateTime,
    getCheckStatusColor,
    getCheckStatusLabel,
    getParentStatusColor,
    getParentStatusLabel,
    addTextColumnTools,
    addDateColumnTools,
    addNumberColumnTools,
} from "./transactionScheduleUi";
import TableMetaBar from "../common/TableMetaBar";
import {
    canDeleteScheduleRow,
    isBulkAgreeEligible,
    isBulkManageEligible,
    isEmptySchedule,
    isFullyAgreed,
} from "./transactionScheduleRules";

function renderCompactText(value, maxWidth = 220) {
    const text = value || "-";

    return (
        <div
            title={text}
            style={{
                maxWidth,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
            }}
        >
            {text}
        </div>
    );
}

function TransactionSchedulesTab({
    filteredTransactions,
    loadingTable,
    openManage,
    openDetails,
    canDelete,
    handleDeleteTransactionSchedule,
    openBulkPreview,
    onBulkAgree,
    onViewInvoice,
    onDownloadInvoice,
    onViewContract,
    onDownloadContract,
}) {
    const [scheduleTableMeta, setScheduleTableMeta] = useState({
        filteredCount: null,
        filters: {},
        sorter: {},
    });

    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [selectedRows, setSelectedRows] = useState([]);

    const handleTableChange = (_, filters, sorter, extra) => {
        setScheduleTableMeta({
            filteredCount: Array.isArray(extra?.currentDataSource)
                ? extra.currentDataSource.length
                : null,
            filters: filters || {},
            sorter: sorter || {},
        });
    };

    const sortedTransactions = useMemo(() => {
        const getPriority = (record) => {
            const total = Number(record.allocationCount || 0);
            const agreed = Number(record.agreedAllocationCount || 0);
            const returned = Number(record.returnedAllocationCount || 0);
            const review = Number(record.reviewAllocationCount || 0);
            const verified = Number(record.verifiedAllocationCount || 0);

            const fullyAgreed = total > 0 && agreed === total;
            const partiallyAgreed = total > 0 && agreed < total;

            if (partiallyAgreed) return 0;
            if (returned > 0) return 1;
            if (review > 0) return 2;
            if (record.parentDisplayStatus === "EMPTY") return 3;
            if (fullyAgreed || verified > 0) return 4;
            return 5;
        };

        return [...filteredTransactions].sort((a, b) => {
            const priorityDiff = getPriority(a) - getPriority(b);
            if (priorityDiff !== 0) return priorityDiff;

            const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
            const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
            return bTime - aTime;
        });
    }, [filteredTransactions]);

    const columns = useMemo(() => {
        const baseColumns = [
            {
                title: "ID",
                dataIndex: "id",
                key: "id",
                width: 80,
                fixed: "left",
            },
            {
                title: "Хүсэлт",
                key: "requestInfo",
                width: 190,
                fixed: "left",
                render: (_, record) => (
                    <div>
                        <div style={{ fontWeight: 600 }}>
                            {record.requestNumber || `#${record.id}`}
                        </div>
                        <div style={{ fontSize: 12, color: "#8c8c8c" }}>
                            {formatDateTime(record.createdAt)}
                        </div>
                    </div>
                ),
            },
            {
                title: "Төлөгч",
                dataIndex: "payer",
                key: "payer",
                width: 220,
                render: (value) => renderCompactText(value, 200),
            },
            {
                title: "Дүн",
                dataIndex: "paymentAmount",
                key: "paymentAmount",
                width: 130,
                render: (value, record) => (
                    <div>
                        <div style={{ fontWeight: 600 }}>{formatAmount(value)}</div>
                        <div style={{ fontSize: 12, color: "#8c8c8c" }}>
                            {record.currency || "-"}
                        </div>
                    </div>
                ),
            },
            {
                title: "Төлөв",
                dataIndex: "parentDisplayStatus",
                key: "parentDisplayStatus",
                width: 170,
                render: (value, record) => {
                    const total = Number(record.allocationCount || 0);
                    const agreed = Number(record.agreedAllocationCount || 0);
                    const hasUnagreed = total > 0 && agreed < total;

                    return (
                        <Space size={6}>
                            <Tag color={getParentStatusColor(value)}>
                                {getParentStatusLabel(value)}
                            </Tag>

                            {hasUnagreed ? (
                                <Badge
                                    color="#fa8c16"
                                    text={
                                        <span style={{ color: "#d46b08", fontSize: 12 }}>
                                            Зөвшөөрөл дутуу
                                        </span>
                                    }
                                />
                            ) : null}
                        </Space>
                    );
                },
            },
            {
                title: "Зөвшөөрөл",
                key: "agreeStatus",
                width: 150,
                render: (_, record) => {
                    const total = Number(record.allocationCount || 0);
                    const agreed = Number(record.agreedAllocationCount || 0);
                    const done = total > 0 && total === agreed;

                    return (
                        <div>
                            <div style={{ fontWeight: 600 }}>
                                {agreed}/{total}
                            </div>
                            <div style={{ fontSize: 12, color: done ? "#389e0d" : "#8c8c8c" }}>
                                {done ? "Бүрэн зөвшөөрөгдсөн" : "Хүлээгдэж байна"}
                            </div>
                        </div>
                    );
                },
            },
            {
                title: "Шалгалт",
                key: "checkSummary",
                width: 220,
                render: (_, record) => {
                    const statuses = record.checkSummaryStatuses || [];
                    if (!statuses.length) return <span style={{ color: "#8c8c8c" }}>-</span>;

                    return (
                        <Space size={[4, 4]} wrap>
                            {statuses.slice(0, 2).map((status) => (
                                <Tag key={status} color={getCheckStatusColor(status)}>
                                    {getCheckStatusLabel(status)}
                                </Tag>
                            ))}
                            {statuses.length > 2 ? <Tag>+{statuses.length - 2}</Tag> : null}
                        </Space>
                    );
                },
            },
            {
                title: "PDF",
                key: "pdfStatus",
                width: 150,
                render: (_, record) => (
                    <Space size={[4, 4]} wrap>
                        <Tag color={record.invoiceFileName ? "green" : "red"}>
                            INV {record.invoiceFileName ? "OK" : "X"}
                        </Tag>
                        <Tag color={record.contractFileName ? "green" : "red"}>
                            CON {record.contractFileName ? "OK" : "X"}
                        </Tag>
                    </Space>
                ),
            },
            {
                title: "Үйлдэл",
                key: "actions",
                width: 150,
                fixed: "right",
                render: (_, record) => {
                    const canDeleteRow = canDeleteScheduleRow(record, canDelete);
                    const emptyRow = isEmptySchedule(record);
                    const agreedRow = isFullyAgreed(record);

                    return (
                        <Space size={6}>
                            <Tooltip
                                title={
                                    hasBothPdfs(record)
                                        ? "Хуваарь удирдах"
                                        : "Нэхэмжлэх болон гэрээ PDF бүрэн оруулсны дараа хуваарь удирдана"
                                }
                            >
                                <span>
                                    <Button
                                        icon={<EditOutlined />}
                                        type={emptyRow ? "primary" : "default"}
                                        ghost={emptyRow}
                                        disabled={!hasBothPdfs(record)}
                                        onClick={() => openManage(record)}
                                    />
                                </span>
                            </Tooltip>

                            <Tooltip title="Дэлгэрэнгүй">
                                <Button
                                    icon={<EyeOutlined />}
                                    type="default"
                                    onClick={() => openDetails(record)}
                                />
                            </Tooltip>

                            {canDelete ? (
                                <Tooltip title={canDeleteRow ? "Устгах" : "Зөвхөн зөвшөөрөгдөөгүй мөрийг устгана"}>
                                    <span>
                                        <Popconfirm
                                            title="Энэ мөрийг устгах уу?"
                                            okText="Тийм"
                                            cancelText="Үгүй"
                                            onConfirm={() => handleDeleteTransactionSchedule(record)}
                                            disabled={!canDeleteRow}
                                        >
                                            <Button
                                                danger
                                                icon={<DeleteOutlined />}
                                                disabled={!canDeleteRow}
                                            />
                                        </Popconfirm>
                                    </span>
                                </Tooltip>
                            ) : null}
                        </Space>
                    );
                },
            },
        ];

        return baseColumns
            .map((col) => {
                if (col.key === "requestInfo" || col.key === "actions" || col.key === "agreeStatus" || col.key === "checkSummary") {
                    return col;
                }

                if (col.dataIndex === "payer") {
                    return addTextColumnTools(col, filteredTransactions);
                }

                if (col.dataIndex === "paymentAmount") {
                    return addNumberColumnTools(col);
                }

                if (col.dataIndex === "createdAt") {
                    return addDateColumnTools(col);
                }

                return col;
            });
    }, [filteredTransactions, openManage, openDetails, canDelete, handleDeleteTransactionSchedule]);

    const bulkManageRows = selectedRows.filter(isBulkManageEligible);
    const bulkAgreeRows = selectedRows.filter(isBulkAgreeEligible);
    // const hasBothPdfs = (record) => !!record.invoiceFileName && !!record.contractFileName;
    const hasBothPdfs = (record) => { return true; };


    const getRowClassName = (record) => {
        const total = Number(record.allocationCount || 0);
        const agreed = Number(record.agreedAllocationCount || 0);
        const returned = Number(record.returnedAllocationCount || 0);
        const review = Number(record.reviewAllocationCount || 0);
        const verified = Number(record.verifiedAllocationCount || 0);

        const fullyAgreed = total > 0 && agreed === total;
        const partiallyAgreed = total > 0 && agreed < total;

        if (partiallyAgreed) return "ts-row-unagreed";
        if (returned > 0) return "ts-row-returned";
        if (review > 0) return "ts-row-review";
        if (record.parentDisplayStatus === "EMPTY") return "ts-row-empty";
        if (fullyAgreed || verified > 0) return "ts-row-verified";

        return "";
    };


    return {
        tabNode: (
            <div className="space-y-3">
                <TableMetaBar
                    total={sortedTransactions.length}
                    filtered={scheduleTableMeta.filteredCount}
                    filters={scheduleTableMeta.filters}
                    sorter={scheduleTableMeta.sorter}
                />

                <Space wrap style={{ marginBottom: 12 }}>
                    <Button
                        disabled={bulkManageRows.length === 0}
                        onClick={() => openBulkPreview(bulkManageRows)}
                    >
                        Бөөнөөр удирдах ({bulkManageRows.length})
                    </Button>

                    <Button
                        type="primary"
                        ghost
                        disabled={bulkAgreeRows.length === 0}
                        onClick={() => onBulkAgree(bulkAgreeRows)}
                    >
                        Бөөнөөр зөвшөөрөх ({bulkAgreeRows.length})
                    </Button>
                </Space>

                <Table
                    rowKey="id"
                    loading={loadingTable}
                    columns={columns}
                    dataSource={sortedTransactions}
                    onChange={handleTableChange}
                    rowClassName={getRowClassName}
                    scroll={{ x: 1200 }}
                    size="middle"
                    pagination={{
                        showSizeChanger: true,
                        defaultPageSize: 20,
                        pageSizeOptions: [10, 20, 50, 100],
                    }}
                    rowSelection={{
                        selectedRowKeys,
                        onChange: (keys, rows) => {
                            setSelectedRowKeys(keys);
                            setSelectedRows(rows);
                        },
                    }}
                    expandable={{
                        expandedRowRender: (record) => (
                            <div style={{ padding: "6px 0" }}>
                                <div><strong>Хүлээн авагч байгууллага:</strong> {record.beneficiaryCompany || "-"}</div>
                                <div><strong>Банк:</strong> {record.beneficiaryBank || "-"}</div>
                                <div><strong>Данс:</strong> {record.beneficiaryAccount || "-"}</div>
                                <div><strong>Гүйлгээний утга:</strong> {record.paymentPurpose || "-"}</div>
                                <div><strong>Буцаалт:</strong> {record.returnedAllocationCount || 0}</div>
                                <div><strong>Гараар шалгах:</strong> {record.reviewAllocationCount || 0}</div>
                                <div><strong>Эцэслэн баталгаажсан:</strong> {record.verifiedAllocationCount || 0}</div>

                                <div style={{ marginTop: 12 }}>
                                    <strong>Хавсралтууд:</strong>

                                    <div
                                        style={{
                                            display: "grid",
                                            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                                            gap: 8,
                                            marginTop: 8,
                                        }}
                                    >
                                        <div
                                            style={{
                                                border: "1px solid #f0f0f0",
                                                borderRadius: 8,
                                                padding: 10,
                                                background: "#fafafa",
                                            }}
                                        >
                                            <div style={{ fontWeight: 600 }}>Нэхэмжлэх</div>
                                            <div
                                                style={{
                                                    fontSize: 12,
                                                    color: "#8c8c8c",
                                                    whiteSpace: "nowrap",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    marginBottom: 8,
                                                }}
                                                title={record.invoiceFileName || "-"}
                                            >
                                                {record.invoiceFileName || "Файл байхгүй"}
                                            </div>

                                            <Space wrap size={8}>
                                                <Button
                                                    size="small"
                                                    icon={<EyeOutlined />}
                                                    disabled={!record.id}
                                                    onClick={() => onViewInvoice?.(record)}
                                                >
                                                    Харах
                                                </Button>
                                                <Button
                                                    size="small"
                                                    icon={<DownloadOutlined />}
                                                    disabled={!record.id}
                                                    onClick={() => onDownloadInvoice?.(record)}
                                                >
                                                    Татах
                                                </Button>
                                            </Space>
                                        </div>

                                        <div
                                            style={{
                                                border: "1px solid #f0f0f0",
                                                borderRadius: 8,
                                                padding: 10,
                                                background: "#fafafa",
                                            }}
                                        >
                                            <div style={{ fontWeight: 600 }}>Гэрээ</div>
                                            <div
                                                style={{
                                                    fontSize: 12,
                                                    color: "#8c8c8c",
                                                    whiteSpace: "nowrap",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    marginBottom: 8,
                                                }}
                                                title={record.contractFileName || "-"}
                                            >
                                                {record.contractFileName || "Файл байхгүй"}
                                            </div>

                                            <Space wrap size={8}>
                                                <Button
                                                    size="small"
                                                    icon={<EyeOutlined />}
                                                    disabled={!record.id}
                                                    onClick={() => onViewContract?.(record)}
                                                >
                                                    Харах
                                                </Button>
                                                <Button
                                                    size="small"
                                                    icon={<DownloadOutlined />}
                                                    disabled={!record.id}
                                                    onClick={() => onDownloadContract?.(record)}
                                                >
                                                    Татах
                                                </Button>
                                            </Space>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ),
                    }}
                />
            </div>
        ),
    };
}

export default TransactionSchedulesTab;